import {httpClientV2} from "../../../../infrastructure/http";
import type {
  LiveEvent,
  PaginatedLiveEvents,
  CreateLiveEventRequest,
  UpdateLiveEventRequest,
  LiveEventFilter,
  PaginationParams,
  LiveEventRepository,
  JoinLiveEventResult,
  RescheduleLiveEventRequest,
} from "../../domain/types";
import {LiveEventMapperV2} from "../mappers/mapper.v2";
import type {
  LiveEventV2ApiResponse,
  JoinLiveEventV2ApiResponse,
  LiveEventUpcomingListV2Response,
  LiveEventPastListV2Response,
} from "../mappers/mapper.types";

/**
 * V2 API implementation of LiveEventRepository.
 *
 * Endpoints:
 * - POST /live-event
 * - GET /live-event/fetch/:id
 * - GET /live-event/list?filter=...&page=...&perPage=...
 * - PATCH /live-event/:id (v2 uses PATCH for update)
 * - DELETE /live-event/:id
 *
 * Pure TypeScript - no React imports.
 */
export class LiveEventRepositoryV2 implements LiveEventRepository {
  async create(request: CreateLiveEventRequest): Promise<LiveEvent> {
    const apiRequest = LiveEventMapperV2.toApiRequest(request);
    const response = await httpClientV2.post<LiveEventV2ApiResponse>(
      "live-event",
      apiRequest
    );
    return LiveEventMapperV2.toDomain(response.data);
  }

  async getById(id: number): Promise<LiveEvent> {
    const response = await httpClientV2.get<LiveEventV2ApiResponse>(
      `live-event/fetch/${id}`
    );
    return LiveEventMapperV2.toDomain(response.data);
  }

  async getList(
    filter: LiveEventFilter,
    params?: PaginationParams
  ): Promise<PaginatedLiveEvents> {
    const endpoint = this.buildListEndpoint(filter, params);

    if (filter === "upcoming") {
      const response = await httpClientV2.get<LiveEventUpcomingListV2Response>(endpoint);
      return LiveEventMapperV2.toDomainUpcomingList(response.data);
    }

    const response = await httpClientV2.get<LiveEventPastListV2Response>(endpoint);
    return LiveEventMapperV2.toDomainPastList(response.data);
  }

  async update(request: UpdateLiveEventRequest): Promise<LiveEvent> {
    const {id, ...updateData} = request;
    const apiRequest = LiveEventMapperV2.toApiRequest(
      updateData as CreateLiveEventRequest
    );
    // v2 uses PATCH for updates
    const response = await httpClientV2.patch<LiveEventV2ApiResponse>(
      `live-event/${id}`,
      apiRequest
    );
    return LiveEventMapperV2.toDomain(response.data);
  }

  async delete(id: number): Promise<void> {
    await httpClientV2.delete(`live-event/${id}`);
  }

  async join(eventId: number): Promise<JoinLiveEventResult> {
    const response = await httpClientV2.get<JoinLiveEventV2ApiResponse>(
      `live-event/${eventId}/join`
    );
    return LiveEventMapperV2.joinToDomain(response.data);
  }

  async reschedule(request: RescheduleLiveEventRequest): Promise<void> {
    const v2Request = LiveEventMapperV2.rescheduleToV2Request(request);
    await httpClientV2.patch(`live-event/${request.eventId}/reschedule`, v2Request);
  }

  private buildListEndpoint(filter: LiveEventFilter, params?: PaginationParams): string {
    const queryParams = new URLSearchParams();

    if (params?.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params?.perPage !== undefined) {
      queryParams.append("perPage", params.perPage.toString());
    }

    const queryString = queryParams.toString();
    const basePath = filter === "upcoming" ? "live-event/upcoming" : "live-event/past";

    return queryString ? `${basePath}?${queryString}` : basePath;
  }
}
